import React from 'react'
import { Alert, Card, CardContent, Chip, Divider, Grid } from '@mui/material'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { draftCustomerIdentity, REQUIRED_MISSING } from 'pages/onboarding/claim/customerIdentity'

// Helper: render a labelled field row
function Field({ label, value }) {
  if (value === null || value === undefined || value === '' || value === false) return null
  return (
    <MDBox display="flex" alignItems="flex-start" mb={0.75}>
      <MDTypography
        variant="caption"
        fontWeight="medium"
        color="text"
        sx={{ minWidth: 160, mt: '1px', fontSize: '1rem' }}
      >
        {label}
      </MDTypography>
      <MDTypography variant="caption" color="text" sx={{ fontSize: '1rem' }}>
        {String(value)}
      </MDTypography>
    </MDBox >
  )
}

// Helper: a field that is always shown; missing values stay visible.
function RequiredField({ label, value }) {
  const missing = value === REQUIRED_MISSING
  return (
    <MDBox display="flex" alignItems="flex-start" mb={0.75}>
      <MDTypography
        variant="caption"
        fontWeight="medium"
        color="text"
        sx={{ minWidth: 160, mt: '1px', fontSize: '1rem' }}
      >
        {label}
      </MDTypography>
      <MDTypography
        variant="caption"
        color={missing ? 'error' : 'text'}
        fontWeight={missing ? 'bold' : 'regular'}
        sx={{ fontSize: '1rem' }}
      >
        {String(value)}
      </MDTypography>
    </MDBox>
  )
}

// Helper: section header
function SectionHeader({ title, count }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1} mb={1.5} mt={1}>
      <MDTypography variant="subtitle2" fontWeight="bold">
        {title}
      </MDTypography>
      {count !== undefined && (
        <Chip label={count} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
      )}
    </MDBox>
  )
}

function ReviewStep({ state, dropdowns, onEditCustomer, lookupsLoaded = true, createdEntityId }) {
  const { entity, addresses, contacts, items } = state

  // One resolved identity for the review, the device assignment and the summary.
  const identity = draftCustomerIdentity(entity, dropdowns, { lookupsLoaded })

  const resolveAddressType = (id) =>
    dropdowns.addressTypes?.find((t) => String(t.address_type_id) === String(id))?.type_name || id

  const resolveStatus = (id) =>
    dropdowns.entityStatusOptions?.find((s) => String(s.status_type_id) === String(id))
      ?.StatusType?.status_name || id

  const resolveContactType = (id) =>
    dropdowns.contactTypes?.find((t) => String(t.contact_type_id) === String(id))?.type_name || id

  const resolveItem = (id) => {
    const found = dropdowns.items?.find((i) => String(i.item_id) === String(id))
    return found ? found.item_name || found.name : id
  }

  const resolveIcon = (id) =>
    dropdowns.icons?.find((i) => String(i.icon_id) === String(id))?.icon_name || id

  const resolveSharedProviders = (providerIds = []) => {
    const selectedIds = new Set(providerIds.map(Number))
    const names = (dropdowns.providers || [])
      .filter((provider) => selectedIds.has(Number(provider.provider_id)))
      .map((provider) => provider.provider_name)

    return names.length ? names.join(', ') : 'Not shared'
  }

  return (
    <MDBox mt={2}>
      {/* ── Customer / Entity ── */}
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <SectionHeader title="Customer" />
        {onEditCustomer && (
          <MDButton variant="text" color="info" size="small" onClick={onEditCustomer}>
            Edit customer
          </MDButton>
        )}
      </MDBox>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={0}>
            <Grid item xs={12} md={6}>
              <RequiredField label="Customer name" value={identity.name} />
              <RequiredField label="Entity Type" value={identity.entityType} />
              {createdEntityId ? <Field label="Customer ID" value={createdEntityId} /> : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <RequiredField label="Company" value={identity.company} />
              <RequiredField label="Status" value={identity.status} />
            </Grid>
          </Grid>
          {identity.missing.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {identity.missing.join(', ')} {identity.missing.length > 1 ? 'are' : 'is'} required. Use Edit customer to
              complete step 1 before submitting.
            </Alert>
          )}
          {identity.unresolved.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {lookupsLoaded
                ? `${identity.unresolved.join(', ')} could not be matched to the loaded options. Your selection is kept; reload the options or choose it again in Customer.`
                : 'Loading customer options…'}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* ── Addresses ── */}
      <SectionHeader title="Addresses" count={addresses.length} />
      {addresses.length === 0 ? (
        <MDTypography variant="body2" color="secondary" mb={2}>
          No addresses added.
        </MDTypography>
      ) : (
        addresses.map((a, idx) => (
          <Card key={a.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <MDTypography variant="caption" fontWeight="bold" color="info" mb={1} display="block">
                Address #{idx + 1}
              </MDTypography>
              <Grid container spacing={0}>
                <Grid item xs={12} md={6}>
                  <Field label="Name" value={a.name} />
                  <Field label="Address Type" value={resolveAddressType(a.address_type_id)} />
                  <Field label="Status" value={resolveStatus(a.status_type_id)} />
                  <Field label="Street Address 1" value={a.street_address1} />
                  <Field label="Street Address 2" value={a.street_address2} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field label="City" value={a.city} />
                  <Field label="State" value={a.state} />
                  <Field label="Postal Code" value={a.postal_code} />
                  <Field label="Country" value={a.country} />
                  <Field label="Latitude" value={a.latitude} />
                  <Field label="Longitude" value={a.longitude} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Contacts ── */}
      <SectionHeader title="Contacts" count={contacts.length} />
      {contacts.length === 0 ? (
        <MDTypography variant="body2" color="secondary" mb={2}>
          No contacts added.
        </MDTypography>
      ) : (
        contacts.map((c, idx) => (
          <Card key={c.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <MDTypography variant="caption" fontWeight="bold" color="info" mb={1} display="block">
                Contact #{idx + 1}
              </MDTypography>
              <Grid container spacing={0}>
                <Grid item xs={12} md={6}>
                  <Field label="First Name" value={c.contact_first_name} />
                  <Field label="Last Name" value={c.contact_last_name} />
                  <Field label="Contact Type" value={resolveContactType(c.contact_type_id)} />
                  <Field label="Status" value={resolveStatus(c.contact_status_type_id)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field label="Email" value={c.email} />
                  <Field label="Phone" value={c.phone} />
                  <Field
                    label="Create User Account"
                    value={c.createUserAccount ? 'Yes' : null}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Tanks / Assets ── */}
      <SectionHeader title="Tanks / Assets" count={items.length} />
      {items.length === 0 ? (
        <MDTypography variant="body2" color="secondary" mb={2}>
          No items configured. You can add them later.
        </MDTypography>
      ) : (
        items.map((item, idx) => {
          const selectedOption = dropdowns.items?.find(
            (opt) => String(opt.item_id) === String(item.item_id),
          )
          const dynamicAttributes = selectedOption?.Attributes || []

          return (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <MDTypography variant="caption" fontWeight="bold" color="info" mb={1} display="block">
                  Item #{idx + 1}
                </MDTypography>
                <Grid container spacing={0}>
                  <Grid item xs={12} md={6}>
                    <Field label="Item" value={resolveItem(item.item_id)} />
                    <Field label="Name" value={item.name} />
                    <Field label="Map Identifier" value={item.map_identifier} />
                    <Field
                      label="Address"
                      value={
                        item.address_index !== undefined && item.address_index !== ''
                          ? `Address #${Number(item.address_index) + 1}`
                          : null
                      }
                    />
                    <Field label="Icon" value={resolveIcon(item.icon_id)} />
                    <Field label="Status" value={resolveStatus(item.status_type_id)} />
                    <Field label="Default Address Item" value={item.is_default ? 'Yes' : null} />
                    <Field label="Shared Providers" value={resolveSharedProviders(item.provider_ids)} />
                  </Grid>

                  {/* Dynamic attributes */}
                  {dynamicAttributes.length > 0 && (
                    <Grid item xs={12} md={6}>
                      {dynamicAttributes.map((attr) => {
                        const rawVal = item.attributes?.[attr.attribute_id]
                        if (rawVal === undefined || rawVal === null || rawVal === '') return null
                        const displayVal =
                          attr.field_type_id === 4
                            ? rawVal === true || rawVal === 'true'
                              ? 'Yes'
                              : 'No'
                            : String(rawVal)
                        return (
                          <Field
                            key={attr.attribute_id}
                            label={attr.label || attr.attribute_name}
                            value={displayVal}
                          />
                        )
                      })}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )
        })
      )}
    </MDBox>
  )
}

export default ReviewStep
